import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ProductBadge } from '@mweb/zappos-ui';

import { cn } from 'helpers/classnames';
import { BadgeStyles } from 'types/badges';
import useWindowSize from 'hooks/useWindowSize';
import { AppState } from 'types/app';
import { selectBadgeMetadata } from 'selectors/badge';

import css from 'styles/components/common/badge.scss';

export interface BadgeProps {
  id: string;
  badgeName?: string;
  badgeStyles?: BadgeStyles[];
  category?: string;
  url?: string;
  classNameWrapper?: string;
  setLabel?: (label: string | HTMLElement) => void;
  productLink?: string;
  onBadgeCategoryClick?: () => void;
}

const Badge = ({ id, badgeName, category, badgeStyles, url, classNameWrapper, setLabel, productLink, onBadgeCategoryClick }: BadgeProps) => {
  const { styles, name, tooltip } = useSelector((state: AppState) => selectBadgeMetadata(state, id)) || {
    name: badgeName,
    styles: badgeStyles
  };

  const { width: windowWidth } = useWindowSize(500); // debounceWait=500

  useEffect(() => {
    if (setLabel) {
      const label = category ? name + ' ' + category : name;
      setLabel(label);
    }
  }, [category, setLabel, name]);

  if (!styles || !name) {
    return null;
  }

  const dynamicBackgroundStyle = styles[0].backgroundColor;
  const dynamicForegroundStyle = styles[0].foregroundColor;

  const badgeCategory = category && (
    <p className={css.category} title={category}>
      {category}
    </p>
  );

  const BadgeWrapper = () => (
    <div className="mt-4">
      <ProductBadge
        fontColor={dynamicForegroundStyle}
        aria-label={`${name} badge. ${tooltip}`}
        background={dynamicBackgroundStyle}
        helpText={tooltip}
      >
        {name}
      </ProductBadge>
    </div>
  );

  const badgeCategoryLink = windowWidth && windowWidth <= 650 ? productLink : url;

  return (
    <div className={cn(css.badgeWrapper, classNameWrapper)}>
      {url && badgeCategory ? (
        <a href={badgeCategoryLink || url} className={css.badgeLink} onClick={onBadgeCategoryClick}>
          <BadgeWrapper />
          {badgeCategory}
        </a>
      ) : (
        <a href={productLink} className="relative">
          <BadgeWrapper />
        </a>
      )}
    </div>
  );
};

export default Badge;
